<template>
  <a-tag :color="statusColor" :class="tagClass">
    <template v-if="showIcon && statusIcon">
      <component :is="statusIcon" class="status-icon" />
    </template>
    {{ statusText }}
  </a-tag>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getStatusConfig, type StatusConfig } from '@/shared/utils/status'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue'

interface Props {
  /** 状态值 */
  status: string
  /** 状态类型 */
  type?: 'project' | 'task' | 'user' | 'document' | 'video'
  /** 是否显示图标 */
  showIcon?: boolean
  /** 自定义状态配置 */
  customConfig?: Record<string, StatusConfig>
  /** 标签大小 */
  size?: 'small' | 'middle' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'project',
  showIcon: false,
  size: 'middle'
})

// 获取状态配置
const statusConfig = computed<StatusConfig>(() => {
  if (props.customConfig && props.customConfig[props.status]) {
    return props.customConfig[props.status]
  }
  return getStatusConfig(props.status, props.type)
})

// 状态颜色
const statusColor = computed(() => statusConfig.value.color)

// 状态文本
const statusText = computed(() => statusConfig.value.text)

// 状态图标
const statusIcon = computed(() => {
  if (statusConfig.value.icon) {
    // 如果配置了自定义图标，可以在这里处理
    return null
  }
  
  // 根据状态类型返回默认图标
  const statusType = statusConfig.value.type
  switch (statusType) {
    case 'success':
      return CheckCircleOutlined
    case 'error':
      return CloseCircleOutlined
    case 'warning':
      return ExclamationCircleOutlined
    case 'processing':
      return SyncOutlined
    default:
      return ClockCircleOutlined
  }
})

// 标签样式类
const tagClass = computed(() => {
  return {
    [`status-tag-${props.size}`]: true,
    [`status-tag-${statusConfig.value.type}`]: true
  }
})
</script>

<style scoped lang="less">
.status-icon {
  margin-right: 4px;
  font-size: 12px;
}

.status-tag-small {
  font-size: 12px;
  padding: 0 6px;
}

.status-tag-middle {
  font-size: 14px;
  padding: 0 8px;
}

.status-tag-large {
  font-size: 16px;
  padding: 0 10px;
}

.status-tag-success {
  :deep(.ant-tag) {
    background-color: #f6ffed;
    border-color: #b7eb8f;
    color: #52c41a;
  }
}

.status-tag-error {
  :deep(.ant-tag) {
    background-color: #fff2f0;
    border-color: #ffccc7;
    color: #ff4d4f;
  }
}

.status-tag-warning {
  :deep(.ant-tag) {
    background-color: #fffbe6;
    border-color: #ffe58f;
    color: #faad14;
  }
}

.status-tag-processing {
  :deep(.ant-tag) {
    background-color: #e6f7ff;
    border-color: #91d5ff;
    color: #1890ff;
  }
}
</style>

